"use client";

/**
 * Hardware Back closes the open overlay instead of leaving the page.
 *
 * On a phone a full-screen sheet IS the page as far as the reader is
 * concerned, so Back should dismiss it (owner, 2026-09-22: "going back
 * when the search bar is open on mobile shouldn't switch the page, just
 * close it ... same for the filters modal, and basically any sheet").
 *
 * How it works: opening pushes one sentinel history entry; a popstate
 * closes the overlay instead of navigating.
 *
 * Closing by any other means (a button, a pick, Escape, a link) must NOT
 * step history back. Most of those closes come with a navigation: the
 * filter panel's "Axtar" pushes the new filters and closes, a menu link
 * navigates and closes. Next commits a push only after its fetch, so at
 * close time the sentinel is still the current entry, and a
 * history.back() there traversed to the old URL and discarded the
 * navigation still in flight: filters never applied, links went nowhere
 * (production, 2026-09-23).
 *
 * So a close leaves the sentinel in place, marked stale, and the stack
 * is repaired lazily without ever racing a navigation:
 *
 *   - the next push while a stale sentinel is current (the navigation
 *     that came with the close, a later filter change, another overlay
 *     opening) REPLACES it, so the stack never grows a dead step;
 *   - a Back press that leaves a stale sentinel for the same URL was a
 *     step the reader could not see, so it continues one more step.
 *
 * Nesting is fine: each overlay pushes its own sentinel and a Back press
 * unwinds them in the order they were opened.
 */
import { useEffect } from "react";

type OverlayState = { overlay?: string } | null;

let seq = 0;
/** The stale sentinel that is the current history entry, if any. */
let stale: { id: string; href: string } | null = null;
let installed = false;

function currentOverlayId(): string | undefined {
  return (window.history.state as OverlayState)?.overlay;
}

function install() {
  if (installed) return;
  installed = true;
  // Wraps whatever pushState is current: Next's app router has already
  // patched it by the time any overlay opens, and its own navigations
  // call window.history.pushState, so they pass through here too.
  const push = window.history.pushState.bind(window.history);
  const replace = window.history.replaceState.bind(window.history);
  window.history.pushState = function pushState(data, unused, url) {
    if (stale && currentOverlayId() === stale.id) {
      stale = null;
      return replace(data, unused, url);
    }
    stale = null;
    return push(data, unused, url);
  };
  window.history.replaceState = function replaceState(data, unused, url) {
    if (stale && currentOverlayId() === stale.id) {
      const target = url != null ? new URL(url, window.location.href).href : window.location.href;
      // Next replaces instead of pushing when the URL did not change
      // (filters applied as they already were). The entry is still the
      // dead duplicate, so it keeps its mark; a real move retires it.
      if (target === stale.href) {
        return replace({ ...(data ?? {}), overlay: stale.id }, unused, url);
      }
      stale = null;
    }
    return replace(data, unused, url);
  };
  // Capture phase, so the skip happens before anything else reacts.
  window.addEventListener("popstate", () => {
    const left = stale;
    stale = null;
    if (left && window.location.href === left.href && currentOverlayId() !== left.id) {
      window.history.back();
    }
  }, true);
}

// How many overlays are open right now. The Telegram Mini App shows its
// native Back button while one is (a press is a history step back, which
// closes the top overlay through the popstate below).
let openOverlays = 0;
const overlayListeners = new Set<() => void>();

function setOpenOverlays(n: number) {
  openOverlays = n;
  overlayListeners.forEach((fn) => fn());
}

export function openOverlayCount(): number {
  return openOverlays;
}

export function subscribeOverlays(fn: () => void): () => void {
  overlayListeners.add(fn);
  return () => { overlayListeners.delete(fn); };
}

export function useBackToClose(open: boolean, close: () => void) {
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    install();
    const id = `o${++seq}`;
    window.history.pushState({ overlay: id }, "");
    setOpenOverlays(openOverlays + 1);
    let consumedByPop = false;
    const onPop = () => {
      // Back popped an overlay opened above this one (a place picker over
      // the filter sheet): this overlay's own sentinel is the current
      // entry again, so it stays open. Only the top one closes.
      if (currentOverlayId() === id) return;
      consumedByPop = true;
      close();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      setOpenOverlays(Math.max(0, openOverlays - 1));
      if (!consumedByPop && currentOverlayId() === id) {
        stale = { id, href: window.location.href };
      }
    };
    // `close` is called from an event handler, so a changing identity
    // must not re-arm the sentinel: only `open` drives this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
